// Per-node OAuth account picker. Renders inside NodeParamsPanel above the
// regular hyperparameter editor for custom + agent nodes whose ACTUAL CODE (or
// existing credentialIds) references one of the supported providers.
//
// Detection scans `node.code` for `<provider>.login(` — NOT the generation
// prompt. Token-consuming nodes (which read an already-fetched token from
// vars/inputs and never call login() themselves) therefore don't show a
// picker; it appears only on the node that actually authenticates.

const React = require('react');
const { createElement: h, useEffect, useState } = React;
const { request, getApiBasePath, getAccessToken } = require('../lib/apiClient');
const { connectProvider, isOAuthProvider } = require('../lib/oauthConnect');

// Add a provider here and the picker, connect popup, and message listener all
// work for it — no other edits required. Labels match ConnectedAccountsPage.
// `pagePopup: true` providers (Uniconta) open a server-served credentials form
// in a popup window (not an OAuth /url round-trip), passing the access token in
// the URL hash; they postMessage the same `<provider>-auth-success` event.
var PROVIDER_CONFIG = {
  google:    { label: 'Google',    urlEndpoint: '/google-auth/url',    popupName: 'google-auth' },
  microsoft: { label: 'Microsoft', urlEndpoint: '/microsoft-auth/url', popupName: 'microsoft-auth' },
  shopify:   { label: 'Shopify',   pagePopup: true, popupName: 'shopify-auth', pagePath: '/shopify-auth/connect-page' },
  slack:     { label: 'Slack',     urlEndpoint: '/slack-auth/url',     popupName: 'slack-auth' },
  atlassian: { label: 'Atlassian', urlEndpoint: '/atlassian-auth/url', popupName: 'atlassian-auth' },
  uniconta:  { label: 'Uniconta',  pagePopup: true, popupName: 'uniconta-auth', pagePath: '/uniconta-auth/connect-page' }
};

var PROVIDER_KEYS = Object.keys(PROVIDER_CONFIG);

// One regex match per provider. \b stops 'mygoogle.login' from triggering
// 'google'. \s* between the dot and login tolerates `google . login()`.
function detectProvidersInCode(source) {
  var text = String(source || '');
  var found = {};
  PROVIDER_KEYS.forEach(function(provider) {
    var re = new RegExp('\\b' + provider + '\\s*\\.\\s*login\\s*\\(', 'i');
    if (re.test(text)) found[provider] = true;
  });
  return found;
}

// Decide which provider rows to show. A provider appears if EITHER:
//   1. its `.login()` shows up in the node's ACTUAL CODE, OR
//   2. the node already has a credentialIds entry for it (so a previously
//      pinned selection stays visible even if the user later removed the
//      relevant code — they can still un-pin from the UI).
//
// We scan node.code only — NOT the generation prompt. A node that merely
// consumes a token (e.g. `uniconta.tables(vars.token)`) shouldn't show an
// account picker just because its prompt documents the login pattern; the
// picker belongs on the node that actually calls login().
function detectProviders(node) {
  var fromCode = detectProvidersInCode(node && node.code);
  var pinned = (node && node.credentialIds) || {};
  PROVIDER_KEYS.forEach(function(provider) {
    if (pinned[provider]) fromCode[provider] = true;
  });
  return PROVIDER_KEYS.filter(function(p) { return fromCode[p]; });
}

function NodeCredentialsSection(props) {
  var node = props.node;
  var onCredentialChange = props.onCredentialChange || function() {};

  var [accountsByProvider, setAccountsByProvider] = useState({});
  var [loading, setLoading] = useState(false);
  var [pendingProvider, setPendingProvider] = useState('');
  var [error, setError] = useState('');

  // Load the user's connected accounts once when this section mounts. Cheap
  // — one request, no refresh tokens cross the wire (the server returns the
  // safe summary). Re-runs only on node id change so switching nodes doesn't
  // refetch repeatedly.
  function fetchAccounts() {
    setLoading(true);
    setError('');
    return request('/connected-accounts')
      .then(function(resp) {
        setAccountsByProvider((resp && resp.accounts) || {});
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not load connected accounts.');
      })
      .then(function() { setLoading(false); });
  }

  useEffect(function() {
    fetchAccounts();
  }, [node && node.id]);

  // Listen for post-OAuth messages from the popup. On success, refetch the
  // accounts list and auto-select the newest credential for the provider that
  // just connected (the new credentialId is whichever id appears in the
  // refreshed list but not in our pre-connect snapshot).
  useEffect(function() {
    function handleMessage(event) {
      var data = event && event.data;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
      var successMatch = data.type.match(/^([a-z]+)-auth-success$/);
      var errorMatch = data.type.match(/^([a-z]+)-auth-error$/);
      if (successMatch) {
        var provider = successMatch[1];
        if (!PROVIDER_CONFIG[provider]) return;
        var beforeIds = ((accountsByProvider[provider] || []).map(function(a) { return a.id; }));
        setPendingProvider('');
        fetchAccounts().then(function() {
          // Use the latest snapshot from setAccountsByProvider's closure isn't
          // available here — fetch again synchronously via the state setter
          // callback to find the new id and auto-select it on this node.
          setAccountsByProvider(function(latest) {
            var list = (latest && latest[provider]) || [];
            var newest = null;
            for (var i = 0; i < list.length; i++) {
              if (beforeIds.indexOf(list[i].id) === -1) { newest = list[i]; break; }
            }
            if (newest && node && node.id) {
              onCredentialChange(node.id, provider, newest.id);
            }
            return latest;
          });
        });
      } else if (errorMatch) {
        setPendingProvider('');
        setError(data.error ? String(data.error) : (PROVIDER_CONFIG[errorMatch[1]].label + ' connection failed.'));
      }
    }
    window.addEventListener('message', handleMessage);
    return function() { window.removeEventListener('message', handleMessage); };
  }, [node && node.id, accountsByProvider]);

  function startConnect(provider) {
    var cfg = PROVIDER_CONFIG[provider];
    if (!cfg) return;
    setError('');
    setPendingProvider(provider);

    // Page-popup providers (Uniconta): open the server-served credentials form
    // directly, passing the access token in the URL hash for the in-popup POST.
    if (cfg.pagePopup) {
      var token = getAccessToken();
      var pageUrl = getApiBasePath() + cfg.pagePath + '#token=' + encodeURIComponent(token || '');
      var pagePopup = window.open(pageUrl, cfg.popupName, 'width=480,height=560,left=200,top=100');
      if (!pagePopup) {
        setPendingProvider('');
        setError('Popup was blocked. Allow popups for this site and try again.');
      }
      return;
    }

    // OAuth providers go through the shared connectProvider flow, which runs the
    // Google scope-picker first and opens the popup. Success/error still arrives
    // via the window `message` listener above (which refetches + auto-selects);
    // this promise just clears the pending state and surfaces failures.
    if (isOAuthProvider(provider)) {
      connectProvider(provider).catch(function(err) {
        setPendingProvider('');
        var msg = err && err.message ? err.message : '';
        if (msg && msg !== 'cancelled') setError(msg);
      });
      return;
    }

    setPendingProvider('');
    setError('Could not start ' + cfg.label + ' login.');
  }

  var providers = detectProviders(node);
  if (providers.length === 0) return null;

  return h('div', { className: 'wf-cred-section' },
    h('div', { className: 'wf-cred-section__title' }, 'Connected accounts for this node'),
    h('div', { className: 'wf-cred-section__hint' },
      'Pick which account this node uses. Leave unset to use your first connected account.'
    ),
    loading && Object.keys(accountsByProvider).length === 0
      ? h('div', { className: 'wf-cred-section__loading' }, 'Loading accounts...')
      : null,
    providers.map(function(provider) {
      var cfg = PROVIDER_CONFIG[provider];
      var rawList = accountsByProvider[provider] || [];
      // Only credentials with a real id can be pinned per node. Legacy
      // single-object connections (from before multi-account shipped) have
      // no id and are implicitly the default — exposing them as dropdown
      // options would let users pick something that resolves to the same
      // account as "Default", which is just confusing.
      var pinnableList = rawList.filter(function(c) { return !!c.id; });
      var hasDefaultOnly = rawList.length > 0 && pinnableList.length === 0;
      var pinned = ((node && node.credentialIds) || {})[provider] || '';
      var pending = pendingProvider === provider;
      return h('div', { className: 'wf-cred-row', key: provider },
        h('div', { className: 'wf-cred-row__label' }, cfg.label + ' account'),
        pinnableList.length > 0
          ? h('select', {
              className: 'wf-cred-row__select',
              value: pinned,
              disabled: pending,
              onChange: function(e) {
                if (node && node.id) onCredentialChange(node.id, provider, e.target.value);
              }
            },
              h('option', { value: '' }, 'Default (workflow owner’s ' + cfg.label + ')'),
              pinnableList.map(function(acc) {
                return h('option', { key: acc.id, value: acc.id }, acc.label || (cfg.label + ' account'));
              })
            )
          : h('div', { className: 'wf-cred-row__empty' },
              hasDefaultOnly
                ? 'Using your connected ' + cfg.label + ' account. Connect another to enable per-node selection.'
                : 'No ' + cfg.label + ' accounts connected yet.'
            ),
        h('button', {
          className: 'wf-cred-row__connect',
          type: 'button',
          disabled: pending,
          onClick: function() { startConnect(provider); }
        }, pending ? 'Opening...' : (rawList.length > 0 ? '+ Connect another' : 'Connect ' + cfg.label))
      );
    }),
    error ? h('div', { className: 'wf-cred-section__error' }, error) : null
  );
}

// Exposed so callers (e.g. NodeParamsPanel's Live credentials-only mode) can ask
// "does this node show an account picker?" without rendering the section.
NodeCredentialsSection.detectProviders = detectProviders;

module.exports = NodeCredentialsSection;
